// Inicio del portal del proveedor (PP-64, rediseño 2026-10-08): al abrir se
// ve lo que hay que hacer y si alcanzan los moviles, de todas las plantas
// juntas o de la filtrada en la barra. Dos niveles, de arriba abajo
// (docs/PLAN-REDISENO-PORTAL.md §6.3):
//
//   "Para hacer ahora":
//   1. franja de cuatro KPIs, cada uno enlace a su seccion: por responder y
//      en cuanto vence la primera, falta asignar, la proxima franja (moviles
//      libres sobre elegibles) y los documentos que vencen esta semana;
//   2. un aviso de severidad alta solo si en la proxima franja faltan moviles;
//   3. ofertas para responder, la que vence primero arriba, hasta N;
//   4. aceptados que falta asignar, hasta N.
//   "Para hoy" (informativo, menos peso):
//   5. salidas de hoy, las que vienen primero y las pasadas plegadas;
//   6. avisos por severidad: vence hoy, asignado con advertencia, salio de
//      la bandeja.
//
// La carga por franja completa vive en Viajes (vista "Carga por franja").
//
// Lee con una lectura multiplanta por bloque (ADR-025) y escribe por planta
// con el mismo puerto que la bandeja: cada fila trae su planta y su
// proveedor. Cada bloque tiene su propio estado: si una lectura falla, el
// resto del Inicio sigue en pantalla. Las acciones confirman con un toast.
//
// Las ofertas salen de la encuesta del portal (offer-alert/OffersFeed.tsx), la
// misma que usa el aviso de oferta nueva en todas las secciones (PP-71): el
// Inicio no pide las ofertas por su cuenta ni tiene aviso propio.
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { Driver, OfferRejectReason, ProviderMultiSiteTrip, SiteVehicle } from "@ucorp/contracts";
import { Alert } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import { Card, PanelHeader } from "../components/ui/card.js";
import { cn } from "../components/ui/cn.js";
import { Failure } from "../components/ui/failure.js";
import { Icon } from "../components/ui/icon.js";
import { SkeletonKpis, SkeletonRows } from "../components/ui/skeleton.js";
import { useToast } from "../components/ui/toast.js";
import type { ExpiriesPort } from "../expiries/index.js";
import type { RosterState } from "../fleet/useRoster.js";
import { documentTypeLabel, eligibilitySummary, expiryDetail, formatCalendarDate } from "../logic/documents.js";
import {
  acceptedToAssign,
  accessKey,
  asProviderTrip,
  assignmentWarnings,
  departedTrips,
  expiringSoon,
  expiringToday,
  HOME_ACTION_ROWS,
  HOME_EXPIRY_DAYS,
  HOME_INFO_ROWS,
  loadsInScope,
  nextSlots,
  offersToRespond,
  rowKey,
  rowsInScope,
  topN,
  vanishedOffers,
} from "../logic/home.js";
import { accessesForSite, hrefFor } from "../logic/navigation.js";
import { lastDriverByVehicle } from "../logic/quick-assign.js";
import { dueDeadlines, formatRemaining, remainingSeconds, tripTitle } from "../logic/requests.js";
import type { MultiSitePort } from "../multi-site/port.js";
import { systemClock, type Clock } from "../requests/clock.js";
import { ACTION_ERROR_COPY } from "../requests/labels.js";
import { RequestsError, type RequestsPort } from "../requests/port.js";
import type { SectionContext } from "../shell/types.js";
import type { SiteAccess } from "../sites/port.js";
import { usePolled } from "../multi-site/usePolled.js";
import { useOffersFeed } from "../offer-alert/index.js";
import { Departures } from "./Departures.js";
import { PendingRow } from "./PendingRow.js";
import { StaleTag } from "./StaleTag.js";
import { useSiteFleet, type SiteFleetState } from "./useSiteFleet.js";

/** Viajes y carga del dia cambian por asignacion, no por reloj: un minuto alcanza. */
export const DAY_POLL_MS = 60_000;
const TICK_MS = 1_000;

interface Props {
  context: SectionContext;
  port: MultiSitePort;
  requestsPort: RequestsPort;
  fleetPort: ExpiriesPort;
  clock?: Clock;
}

type Tone = "info" | "aviso" | "error" | "ok";
interface Outcome {
  tone: Tone;
  text: string;
}

function rosterOf<T>(fleet: SiteFleetState, row: ProviderMultiSiteTrip, pick: (r: { vehicles: readonly SiteVehicle[]; drivers: readonly Driver[] }) => readonly T[]): RosterState<T> {
  if (fleet.estado !== "listo") return fleet;
  const roster = fleet.rosters.get(accessKey(row));
  if (roster === undefined) return { estado: "error" };
  return { estado: "listo", today: roster.today, items: pick(roster) };
}

/** Celda de la franja de KPIs: rotulo, valor grande en tabulares y detalle; enlace a su seccion. */
function Kpi({ label, value, detail, tone, href }: { label: string; value: React.ReactNode; detail: string; tone?: "alerta" | "aviso"; href: string }): React.JSX.Element {
  return (
    <a
      href={href}
      className="flex min-w-0 flex-col border-l border-divisor px-4 py-2.5 no-underline first:border-l-0 hover:bg-velo hover:no-underline max-md:[&:nth-child(-n+2)]:border-t-0 max-md:[&:nth-child(2n+1)]:border-l-0 max-md:[&:nth-child(n+3)]:border-t"
    >
      <span className="truncate text-micro font-medium uppercase tracking-eyebrow text-texto-debil">{label}</span>
      <span className={cn("tabular mt-1 text-xl font-semibold leading-none tracking-display", tone === "alerta" ? "text-naranja-texto" : tone === "aviso" ? "text-ambar" : "text-blanco")}>
        {value}
      </span>
      <span className="mt-1 line-clamp-2 text-eyebrow leading-snug text-texto-suave">{detail}</span>
    </a>
  );
}

export function Home({ context, port, requestsPort, fleetPort, clock = systemClock }: Props): React.JSX.Element {
  const baseId = useId();
  const { session } = context;
  // El shell puede pasar un arreglo nuevo en cada render: la clave lo estabiliza.
  const sitesKey = JSON.stringify(context.sites);
  const sites = useMemo(() => JSON.parse(sitesKey) as SiteAccess[], [sitesKey]);
  // Las habilitaciones del filtro: todas, o las de la planta elegida.
  const accesses = useMemo(
    () => (context.siteId === null ? sites : accessesForSite(sites, context.siteId)),
    [sites, context.siteId],
  );

  const offers = useOffersFeed();
  const { notify } = useToast();
  const readTrips = useCallback(() => port.trips(session), [port, session]);
  const readLoad = useCallback(() => port.slotLoad(session), [port, session]);
  const trips = usePolled(readTrips, DAY_POLL_MS, clock);
  const load = usePolled(readLoad, DAY_POLL_MS, clock);
  const fleet = useSiteFleet(fleetPort, session, accesses);

  const [now, setNow] = useState(() => clock.now());
  useEffect(() => {
    setNow(clock.now());
    return clock.every(TICK_MS, () => setNow(clock.now()));
  }, [clock]);

  // Ofertas de todas las habilitaciones de la cuenta (para los relojes y
  // "salio de la bandeja") y las del filtro (las que se pintan).
  const offerItems = offers.state.estado === "listo" ? offers.state.data.items : undefined;
  const allOffers = useMemo(
    () => (offerItems === undefined ? undefined : rowsInScope(offerItems, sites)),
    [offerItems, sites],
  );
  const scopedOffers = useMemo(() => (allOffers === undefined ? [] : rowsInScope(allOffers, accesses)), [allOffers, accesses]);

  // Un reloj que llega a cero: una sola consulta por reloj (como la bandeja).
  const fired = useRef(new Set<string>());
  const refreshOffers = offers.refresh;
  useEffect(() => {
    if (allOffers === undefined) return;
    const due = dueDeadlines(allOffers.map(asProviderTrip), now).filter((key) => !fired.current.has(key));
    if (due.length === 0) return;
    for (const key of due) fired.current.add(key);
    refreshOffers();
  }, [allOffers, now, refreshOffers]);

  // Una oferta que deja de venir: se pregunta por los viajes de su dia. Si ya
  // no es de la empresa, salio de la bandeja (vencio, paso a otro proveedor o
  // la planta la cancelo). Nunca se dice a que proveedor paso.
  const previousOffers = useRef<readonly ProviderMultiSiteTrip[] | undefined>(undefined);
  const rejected = useRef(new Set<string>());
  const mounted = useRef(true);
  const [departed, setDeparted] = useState<readonly ProviderMultiSiteTrip[]>([]);
  const refreshTrips = trips.refresh;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (allOffers === undefined) return;
    const before = previousOffers.current;
    previousOffers.current = allOffers;
    if (before === undefined) return;
    const vanished = vanishedOffers(before, allOffers);
    if (vanished.length === 0) return;
    refreshTrips();
    const dates = [...new Set(vanished.map((row) => row.date))];
    Promise.all(dates.map((date) => port.trips(session, date)))
      .then((reads) => {
        if (!mounted.current) return;
        const gone = departedTrips(vanished, reads.flatMap((read) => read.items), rejected.current);
        if (gone.length === 0) return;
        const keys = new Set(gone.map(rowKey));
        setDeparted((list) => [...list.filter((row) => !keys.has(rowKey(row))), ...gone]);
      })
      // Sin la lectura no se puede confirmar: no se avisa algo que puede no haber pasado.
      .catch(() => undefined);
  }, [allOffers, port, session, refreshTrips]);

  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const refreshFleet = fleet.refresh;

  const withBusy = useCallback(
    async (row: ProviderMultiSiteTrip, operation: () => Promise<Outcome>): Promise<boolean> => {
      const key = rowKey(row);
      setBusy((set) => new Set(set).add(key));
      try {
        const outcome = await operation();
        notify(outcome.text, outcome.tone);
        refreshOffers();
        refreshTrips();
        return true;
      } catch (error: unknown) {
        const kind = error instanceof RequestsError ? error.kind : "unavailable";
        notify(ACTION_ERROR_COPY[kind], "error");
        if (kind !== "unavailable") {
          refreshOffers();
          refreshTrips();
        }
        return false;
      } finally {
        setBusy((set) => {
          const next = new Set(set);
          next.delete(key);
          return next;
        });
      }
    },
    [notify, refreshOffers, refreshTrips],
  );

  const timezones = useMemo(() => {
    const map = new Map<string, string>();
    for (const state of [offers.state, trips.state]) {
      if (state.estado === "listo") for (const site of state.data.sites) map.set(site.siteId, site.timezone);
    }
    if (load.state.estado === "listo") for (const site of load.state.data.sites) map.set(site.siteId, site.timezone);
    return map;
  }, [offers.state, trips.state, load.state]);

  const todays = useMemo(() => {
    const map = new Map<string, string>();
    if (load.state.estado === "listo") for (const site of load.state.data.sites) map.set(site.siteId, site.date);
    if (trips.state.estado === "listo") for (const site of trips.state.data.sites) if (site.date !== undefined) map.set(site.siteId, site.date);
    return map;
  }, [trips.state, load.state]);

  const title = (row: ProviderMultiSiteTrip) => `${tripTitle(asProviderTrip(row), timezones.get(row.siteId))} (${row.siteName})`;
  const target = (row: ProviderMultiSiteTrip) => ({ siteId: row.siteId, providerId: row.providerId });

  const onAccept = (row: ProviderMultiSiteTrip, offerId: number) =>
    withBusy(row, async () => {
      await requestsPort.accept(session, target(row), row.tripId, offerId);
      return { tone: "ok", text: `Oferta aceptada: ${title(row)}. Falta asignar vehículo y conductor antes de que venza el tiempo.` };
    });

  const onReject = (row: ProviderMultiSiteTrip, offerId: number, reason: OfferRejectReason) =>
    withBusy(row, async () => {
      await requestsPort.reject(session, target(row), row.tripId, offerId, reason);
      rejected.current.add(rowKey(row));
      return { tone: "info", text: `Oferta rechazada: ${title(row)}. El viaje sale de la bandeja de la empresa.` };
    });

  const onAssign = (row: ProviderMultiSiteTrip, vehicleId: string, driverId: string) =>
    withBusy(row, async () => {
      const result = await requestsPort.assign(session, target(row), row.tripId, { by: "id", vehicleId, driverId });
      refreshFleet();
      if (result.eligible) return { tone: "ok", text: `Vehículo y conductor asignados: ${title(row)}.` };
      const reasons = [
        result.eligibility.vehicle.eligible ? undefined : `vehículo: ${eligibilitySummary(result.eligibility.vehicle).detail}`,
        result.eligibility.driver.eligible ? undefined : `conductor: ${eligibilitySummary(result.eligibility.driver).detail}`,
      ].filter((line): line is string => line !== undefined);
      return {
        tone: "aviso",
        text: `Asignado con advertencia: ${title(row)}. No elegible en esta planta (${reasons.join("; ")}). La planta recibe la alerta.`,
      };
    });

  const refreshAll = () => {
    refreshOffers();
    refreshTrips();
    load.refresh();
    refreshFleet();
  };

  const toRespond = offersToRespond(scopedOffers);
  const toAssign = acceptedToAssign(scopedOffers);
  const respond = topN(toRespond, HOME_ACTION_ROWS);
  const assign = topN(toAssign, HOME_ACTION_ROWS);
  const scopedTrips = trips.state.estado === "listo" ? rowsInScope(trips.state.data.items, accesses) : [];
  const scopedLoads = load.state.estado === "listo" ? loadsInScope(load.state.data.sites, accesses) : [];
  const rosters = fleet.state.estado === "listo" ? fleet.state.rosters : undefined;
  const scopedDeparted = rowsInScope(departed, accesses);
  const warnings = rosters === undefined ? [] : assignmentWarnings(scopedTrips, rosters);
  const documents = fleet.state.estado === "listo" ? expiringSoon(fleet.state.expiries) : [];
  const documentsToday = expiringToday(documents);
  const shownDocuments = topN(documents, HOME_INFO_ROWS);
  const upcomingSlots = nextSlots(scopedLoads, now);
  const shortSlots = upcomingSlots.filter((slot) => slot.short);

  const tripsHref = hrefFor({ path: "/trips", siteId: context.siteId });
  const expiriesHref = hrefFor({ path: "/expiries", siteId: context.siteId });
  const loadHref = (siteId: string) => hrefFor({ path: "/trips", siteId }, "carga");

  const pendingRow = (row: ProviderMultiSiteTrip) => (
    <PendingRow
      key={rowKey(row)}
      row={row}
      now={now}
      timezone={timezones.get(row.siteId)}
      today={todays.get(row.siteId)}
      busy={busy.has(rowKey(row))}
      vehicles={rosterOf(fleet.state, row, (r) => r.vehicles)}
      drivers={rosterOf(fleet.state, row, (r) => r.drivers)}
      // Los ids de vehiculo son locales de la planta: solo cuentan sus viajes.
      lastDrivers={lastDriverByVehicle(scopedTrips.filter((t) => t.siteId === row.siteId), row.providerId, row.tripId)}
      onAccept={onAccept}
      onReject={onReject}
      onAssign={onAssign}
    />
  );

  // KPIs: el primer reloj de cada lista y la proxima franja de la primera
  // planta del filtro (con "Todas", la que tenga la franja mas corta de moviles).
  const firstRespond = toRespond[0];
  const firstAssign = toAssign[0];
  const kpiSlot = shortSlots[0] ?? upcomingSlots[0];
  const kpis = (
    <div role="group" aria-label="Resumen del turno" className="grid grid-cols-2 overflow-hidden rounded-lg border border-linea bg-superficie md:grid-flow-col md:auto-cols-fr">
      <Kpi
        href={tripsHref}
        label="Por responder"
        value={toRespond.length}
        detail={firstRespond?.offer === undefined ? "sin ofertas abiertas" : `la primera vence en ${formatRemaining(remainingSeconds(firstRespond.offer.deadlineAt, now))}`}
        {...(toRespond.length > 0 ? { tone: "alerta" as const } : {})}
      />
      <Kpi
        href={tripsHref}
        label="Falta asignar"
        value={toAssign.length}
        detail={firstAssign?.offer === undefined ? "nada aceptado sin móvil" : `el primero vence en ${formatRemaining(remainingSeconds(firstAssign.offer.deadlineAt, now))}`}
      />
      <Kpi
        href={kpiSlot === undefined ? tripsHref : loadHref(kpiSlot.siteId)}
        label="Próxima franja"
        value={
          kpiSlot === undefined ? "0" : (
            <>
              {kpiSlot.slot.freeVehicleCount}
              <span className="text-texto-debil">/{kpiSlot.eligibleVehicleCount}</span>
            </>
          )
        }
        detail={
          kpiSlot === undefined
            ? "sin viajes con horario"
            : `${kpiSlot.slot.start} · ${kpiSlot.siteName} · ${String(kpiSlot.slot.freeVehicleCount)} ${kpiSlot.slot.freeVehicleCount === 1 ? "libre" : "libres"} para ${String(kpiSlot.slot.unassignedTripCount)} sin asignar`
        }
        {...(kpiSlot?.short === true ? { tone: "alerta" as const } : {})}
      />
      <Kpi
        href={expiriesHref}
        label="Documentos"
        value={documents.length}
        detail={documents.length === 0 ? "nada vence esta semana" : `vencen esta semana · ${String(documentsToday.length)} ${documentsToday.length === 1 ? "vence" : "vencen"} hoy`}
        {...(documentsToday.length > 0 ? { tone: "aviso" as const } : {})}
      />
    </div>
  );

  const actionBlock = (id: string, label: string, list: ReturnType<typeof topN<ProviderMultiSiteTrip>>, total: number, listLabel: string, emptyText: string) => (
    <Card role="region" aria-labelledby={id} className="gap-0 p-0">
      <PanelHeader id={id} title={label} count={total}>
        {offers.state.estado === "listo" && offers.state.stale && <StaleTag />}
        {list.rest > 0 && (
          <a href={tripsHref} className="font-medium text-acero-texto">
            {`Ver ${list.rest === 1 ? "el otro" : `los otros ${String(list.rest)}`} en Viajes`}
          </a>
        )}
      </PanelHeader>
      {list.shown.length === 0 ? (
        <p role="status" className="m-0 px-3.5 py-3 text-sm text-texto-suave">
          {emptyText}
        </p>
      ) : (
        <ul aria-label={listLabel} className="m-0 list-none p-0">
          {list.shown.map(pendingRow)}
        </ul>
      )}
    </Card>
  );

  const pendingBlocks = () => {
    if (offers.state.estado === "cargando") {
      return (
        <Card className="gap-0 p-0">
          <PanelHeader title="Para responder ahora" />
          <SkeletonRows rows={2} label="Consultando las ofertas de las plantas" />
        </Card>
      );
    }
    if (offers.state.estado === "denegado") {
      return <Failure title="La cuenta no tiene acceso a las ofertas.">Salir e ingresar con una cuenta de operador de proveedor.</Failure>;
    }
    if (offers.state.estado === "error") {
      return <Failure title="No se pudieron consultar las ofertas. Se reintenta sola en unos segundos." onRetry={refreshOffers} />;
    }
    if (toRespond.length === 0 && toAssign.length === 0) {
      return (
        <Card className="gap-1 py-4">
          <p role="status" className="m-0 text-sm text-texto">
            Nada pendiente: no hay ofertas para responder ni viajes aceptados para asignar.
          </p>
          <p className="m-0 text-xs text-texto-suave">Cuando una planta ofrezca un viaje, aparece acá con el tiempo para responder y suena una vez.</p>
        </Card>
      );
    }
    return (
      <>
        {actionBlock(`${baseId}-respond`, "Para responder ahora", respond, toRespond.length, "Ofertas para responder", "No hay ofertas para responder.")}
        {actionBlock(`${baseId}-assign`, "Falta asignar", assign, toAssign.length, "Viajes para asignar", "No hay viajes aceptados sin vehículo y conductor.")}
      </>
    );
  };

  return (
    <section aria-labelledby={`${baseId}-title`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 id={`${baseId}-title`} className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">
            Inicio
          </h1>
          <p className="m-0 text-xs text-texto-suave">
            {context.siteId === null ? "Todas las plantas de la empresa" : `Planta ${accesses[0]?.name ?? ""}`} · horarios en la zona de cada planta · se actualiza solo
          </p>
        </div>
        <Button variant="secundario" size="sm" onClick={refreshAll}>
          <Icon name="actualizar" />
          Actualizar ahora
        </Button>
      </div>

      {offers.state.estado === "cargando" && load.state.estado === "cargando" ? <SkeletonKpis /> : kpis}

      {offers.state.estado === "listo" && offers.state.data.nextCursor !== undefined && (
        <Alert variant="aviso">Hay más ofertas de las que se muestran. Revisar con UCORP.</Alert>
      )}

      {shortSlots.map((slot) => (
        <Alert key={slot.siteId} variant="aviso" className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-l-naranja">
          <span className="font-semibold text-naranja-texto">
            ⚠ Faltan móviles de {slot.slot.start}{slot.end === "" ? "" : ` a ${slot.end}`} en {slot.siteName}
          </span>
          <span className="text-texto-suave">
            {String(slot.slot.tripCount)} viajes, {String(slot.slot.unassignedTripCount)} sin asignar, {String(slot.slot.freeVehicleCount)} móviles sin viaje en la franja. Es una aproximación: no sabe si hay conductor.
          </span>
          <a href={loadHref(slot.siteId)} className="font-medium text-acero-texto md:ml-auto">
            Ver la carga del día
          </a>
        </Alert>
      ))}

      {pendingBlocks()}

      <div className="mt-2">
        <h2 className="m-0 text-base font-semibold text-texto-2">Para hoy</h2>
        <p className="m-0 text-xs text-texto-suave">Informativo: lo que ya tiene móvil y lo que conviene saber. No exige acción inmediata.</p>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Departures
          id={`${baseId}-departures`}
          state={trips.state}
          rows={scopedTrips}
          timezones={timezones}
          rosters={rosters}
          truncated={trips.state.estado === "listo" && trips.state.data.nextCursor !== undefined}
          now={now}
          tripsHref={tripsHref}
          onRetry={refreshTrips}
        />

        <Card role="region" aria-labelledby={`${baseId}-notices`} className="gap-0 p-0">
          <PanelHeader id={`${baseId}-notices`} title="Avisos" count={fleet.state.estado === "listo" ? scopedDeparted.length + warnings.length + documents.length : undefined}>
            <a href={expiriesHref} className="font-medium text-acero-texto">
              Vencimientos
            </a>
          </PanelHeader>
          <div className="flex flex-col gap-2 p-3.5">
            {documentsToday.length > 0 && (
              <Alert variant="aviso" className="border-l-naranja">
                <span className="font-semibold text-naranja-texto">⚠ {documentsToday.length === 1 ? "Vence hoy un documento" : `Vencen hoy ${String(documentsToday.length)} documentos`}</span>
                <span className="text-texto-suave"> · un documento vencido deja al móvil o al conductor no elegible en la planta.</span>
              </Alert>
            )}
            {scopedDeparted.length > 0 && (
              <Alert variant="info" className="flex flex-col gap-1 border-l-texto-debil">
                {scopedDeparted.map((row) => (
                  <span key={rowKey(row)} className="block">
                    Salió de la bandeja: {title(row)}. Se venció el tiempo y pasó a otro proveedor, o la planta lo canceló.
                  </span>
                ))}
                <Button variant="enlace" size="inline" className="self-start text-xs" onClick={() => setDeparted([])}>
                  Cerrar aviso
                </Button>
              </Alert>
            )}
            {warnings.map(({ row, vehicle, driver }) => (
              <Alert key={rowKey(row)} variant="aviso">
                Asignado con advertencia: {title(row)}.
                {vehicle !== undefined && ` Vehículo ${vehicle.code}: ${eligibilitySummary(vehicle.eligibility).detail}.`}
                {driver !== undefined && ` Conductor ${driver.code}: ${eligibilitySummary(driver.eligibility).detail}.`}
              </Alert>
            ))}
            {fleet.state.estado === "cargando" && <SkeletonRows rows={1} label="Consultando la nómina y los documentos" />}
            {(fleet.state.estado === "error" || fleet.state.estado === "denegado") && (
              <Failure title="No se pudieron consultar la nómina y los documentos. Revisar en Vencimientos." onRetry={refreshFleet} />
            )}
            {fleet.state.estado === "listo" && fleet.state.failedSites.length > 0 && (
              <Alert variant="aviso">
                No se pudo consultar la nómina de {fleet.state.failedSites.join(", ")}: los avisos pueden estar incompletos.
              </Alert>
            )}
            {documents.length > 0 && (
              <div className="flex flex-col gap-1">
                <h3 className="m-0 text-xs font-semibold text-texto">
                  Documentos vencidos o que vencen en los próximos {HOME_EXPIRY_DAYS} días ({documents.length})
                </h3>
                <ul aria-label="Documentos por vencer" className="m-0 flex list-none flex-col gap-1 p-0 text-sm text-texto-2">
                  {shownDocuments.shown.map((entry) => (
                    <li key={entry.documentId}>
                      {documentTypeLabel(entry.documentType)} de {entry.subject.type === "vehicle" ? "vehículo" : "conductor"} {entry.subject.code} ·{" "}
                      {entry.subject.detail}:{" "}
                      <span className="tabular">{entry.expiresAt === null ? "no vence" : `${formatCalendarDate(entry.expiresAt)}, ${expiryDetail(entry.expiresAt, entry.today)}`}</span>
                      <span className="text-texto-suave"> ({entry.sites.map((s) => s.name).join(", ")})</span>
                    </li>
                  ))}
                </ul>
                <p className="m-0 text-xs text-texto-suave">
                  {shownDocuments.rest > 0 ? `${String(shownDocuments.rest)} más. ` : ""}
                  <a href={expiriesHref} className="text-acero-texto">
                    Se renuevan desde Vencimientos.
                  </a>
                </p>
              </div>
            )}
            {scopedDeparted.length === 0 && warnings.length === 0 && documents.length === 0 && fleet.state.estado === "listo" && (
              <p role="status" className="m-0 text-sm text-texto-suave">
                Sin avisos.
              </p>
            )}
          </div>
        </Card>
      </div>
    </section>
  );
}
